import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Bookmark } from 'lucide-react';
import { OrderTimelineReservationDto } from '../../../../../types/orderTimeline';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface TimelineReservationsProps {
  reservations: OrderTimelineReservationDto[];
}

export const TimelineReservations: React.FC<TimelineReservationsProps> = ({ reservations }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Bookmark className="w-4 h-4" />
        {t('manager.modules.orderTimeline.reservations.title', { '0': reservations.length }, `Reservasjoner (${reservations.length})`)}
      </button>

      {expanded && (
        <div className="overflow-x-auto">
          {reservations.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-4 bg-white rounded-lg border border-gray-200">
              {t('manager.modules.orderTimeline.reservations.empty', {}, 'Ingen aktive reservasjoner for denne ordren')}
            </div>
          ) : (
            <table className="w-full text-xs border-collapse bg-white rounded-lg border border-gray-200">
              <thead>
                <tr className="bg-gray-50 text-gray-600">
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('manager.modules.orderTimeline.stockTransactions.cols.id', {}, 'ID')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('manager.modules.orderTimeline.orderLines.cols.itemNo', {}, 'Varenr')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('warehouse.general.bin', {}, 'Lokasjon')}</th>
                  <th className="text-right px-2 py-1.5 font-medium border-b border-gray-200">{t('manager.modules.orderTimeline.pickingLists.cols.reserved', {}, 'Reservert')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('common.fields.status', {}, 'Status')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('manager.modules.orderTimeline.reservations.cols.orderLine', {}, 'Ordre-Linje')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('manager.modules.orderTimeline.orderLines.cols.pList', {}, 'PList')}</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-2 py-1.5 text-gray-400">{r.id}</td>
                    <td className="px-2 py-1.5 font-mono font-medium text-gray-800">{r.itemNo}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-gray-600">{r.binCode || '–'}</td>
                    <td className="px-2 py-1.5 text-right font-medium text-gray-800">{r.qtyReserved}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        r.resStatus === 0 ? 'bg-green-100 text-green-700' :
                        r.resStatus === 1 ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {r.resStatus === 0 ? t('manager.modules.orderTimeline.reservations.statusActive', {}, 'Aktiv') : r.resStatus === 1 ? t('manager.modules.orderTimeline.reservations.statusReserved', {}, 'Reservert') : t('manager.modules.orderTimeline.reservations.statusOther', { '0': r.resStatus }, `Status ${r.resStatus}`)}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-gray-600">{r.orderNo}-{r.lineNo}</td>
                    <td className="px-2 py-1.5 text-gray-600">
                      {r.pListID ? `${r.pListID}-${r.pListLineNo}` : '–'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};
